import "./eventList.css";
import React, {
	useEffect,
	useState,
	useCallback,
	useMemo,
	useRef,
} from "react";
import { FaAnglesDown } from "react-icons/fa6";
import { useLocation } from "react-router-dom";
import moment from "moment";
import {
	getOrdinal,
	getClassForDate,
	modeFormatters,
} from "../../../utils/helper.js";
import { useAppContext } from "../../../context/AppContext.js";
import { DateToggleControl, EventCard, Modal } from "../../../components";
import { ViewEvent, EditEvent, SuccessModal } from "../../../components/Modal";
import { Skeleton } from "@mui/material";
import { ToastContainer } from "react-toastify";
import DarkLoader from "./../../../components/Loader/DarkLoader/DarkLoader.jsx";
import TokenService from "../../../services/token.service.js";

function EventList() {
	const location = useLocation();
	const {
		allEventData,
		filteredEventData,
		setFilteredEventData,
		loadingAllEvents,
		resetStatusToggle,
		setResetStatusToggle,
		setResetSearchBar,
	} = useAppContext();
	const currentStatus = TokenService.getEventStatus("status");

	const [view, setView] = useState("Event List");
	const [currentMode, setCurrentMode] = useState("year");
	const [nextMode, setNextMode] = useState("Week");

	const [headerDateRange, setHeaderDateRange] = useState("");
	const [viewedCalendarYear, setViewedCalendarYear] = useState(
		new Date().getFullYear()
	);
	const [dates, setDates] = useState([]);

	const currentDate = moment().format("D MMM YYYY");

	const [statusFilter, setStatusFilter] = useState(currentStatus || "all");
	const [viewingFirstEvent, setViewingFirstEvent] = useState(false);
	const [viewingLastEvent, setViewingLastEvent] = useState(true);
	const [viewSkipButton, setViewSkipButton] = useState(false);
	const [dateRangeStart, setDateRangeStart] = useState(moment().startOf("day"));
	const [dateRangeEnd, setDateRangeEnd] = useState(
		moment().add(6, "days").endOf("day")
	);

	const [showModal, setShowModal] = useState(false);
	const [modalContent, setModalContent] = useState(null);

	const [shouldScroll, setShouldScroll] = useState(false);
	const dateRef = useRef(null);

	// Reset Status Toggle:
	useEffect(() => {
		if (resetStatusToggle || loadingAllEvents) {
			setStatusFilter("all");
			TokenService.removeEventStatus("status");
		}
	}, [loadingAllEvents, resetStatusToggle]);

	useEffect(() => {
		if (shouldScroll && dateRef.current) {
			dateRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	}, [shouldScroll]);

	// Toggle between modes:
	const toggleModes = useCallback(() => {
		switch (currentMode) {
			case "year":
				setCurrentMode("week");
				setNextMode("Month");
				break;
			case "week":
				setCurrentMode("month");
				setNextMode("Quarter");
				break;
			case "month":
				setCurrentMode("quarter");
				setNextMode("Year");
				break;
			case "quarter":
				setCurrentMode("year");
				setNextMode("Week");
				break;
			default:
				setCurrentMode("year");
				setNextMode("Week");
				break;
		}

		setViewingFirstEvent(false);
	}, [currentMode]);

	// Filter Events by Status:
	const handleViewByStatus = (status) => {
		if (status === statusFilter) {
			setStatusFilter("all");
			setFilteredEventData(allEventData);
			TokenService.removeEventStatus("status");
			return;
		}

		const searchTerm = status;
		const filteredData = allEventData.filter((data) => {
			return data.status.toLowerCase().includes(searchTerm.toLowerCase());
		});

		setStatusFilter(status);
		setFilteredEventData(filteredData);
		TokenService.setEventStatus(status);

		setResetStatusToggle(false);
		setResetSearchBar(true);
	};

	// Handle date format for header:
	const handleEventDateFormat = useCallback(() => {
		const eventsForCurrentYear = filteredEventData.filter((event) => {
			const eventYear = new Date(event.startTime).getFullYear();
			return eventYear === +viewedCalendarYear;
		});

		const formatter = modeFormatters[currentMode.toLowerCase()];

		if (formatter) {
			const dateRange = formatter.dateRange(eventsForCurrentYear);
			const uniqueFormattedDates =
				formatter.uniqueFormatted(eventsForCurrentYear);

			setHeaderDateRange(dateRange);
			setDates(uniqueFormattedDates);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentMode, filteredEventData]);

	// Find Happening Event(s):
	const happeningEvent = useCallback(() => {
		const dateToScrollTo = moment().utc().format("DD.MM");
		const eventToScrollTo = filteredEventData?.find(
			(event) =>
				moment(event?.startTime).utc().format("DD.MM") === dateToScrollTo
		);

		if (eventToScrollTo) {
			setShouldScroll(true);
		}
	}, [filteredEventData]);

	useEffect(() => {
		handleEventDateFormat();
		happeningEvent();
	}, [handleEventDateFormat, happeningEvent]);

	// Filter Events By Mode:
	const filterEventsByMode = useCallback(() => {
		return filteredEventData?.filter((event) => {
			switch (currentMode) {
				case "year":
					if (moment(event?.startTime).utc().year() === +viewedCalendarYear) {
						return moment(event?.startTime).utc().format("DD.MM");
					}
					return false;

				case "week":
					if (moment(event?.startTime).utc().year() === +viewedCalendarYear) {
						return moment(event?.startTime).utc().week();
					}
					return false;

				case "month":
					if (moment(event?.startTime).utc().year() === +viewedCalendarYear) {
						return moment(event?.startTime).utc().format("MMM");
					}
					return false;

				case "quarter":
					if (moment(event?.startTime).utc().year() === +viewedCalendarYear) {
						return moment(event?.startTime).utc().quarter();
					}
					return false;

				default:
					return null;
			}
		});
	}, [currentMode, filteredEventData, viewedCalendarYear]);

	const filteredEvents = useMemo(
		() => filterEventsByMode(),
		[filterEventsByMode]
	);

	// View Events Backwards:
	const viewBackwardsEvents = useCallback(() => {
		if (currentMode === "year") {
			const newDateRangeEnd = dateRangeStart
				.clone()
				.subtract(1, "day")
				.endOf("day");
			const newDateRangeStart = newDateRangeEnd
				.clone()
				.subtract(6, "days")
				.startOf("day");

			setDateRangeEnd(newDateRangeEnd);
			setDateRangeStart(newDateRangeStart);

			const formattedDateRange = `${newDateRangeStart.format(
				"D MMM"
			)} - ${newDateRangeEnd.format("D MMM YYYY")}`;

			setViewedCalendarYear(newDateRangeStart.format("YYYY"));
			setHeaderDateRange(formattedDateRange);

			const filteredEvents = filteredEventData?.filter((event) => {
				const eventDate = moment.utc(event?.startTime);
				return eventDate.isBetween(
					newDateRangeStart,
					newDateRangeEnd,
					"day",
					"[]"
				);
			});

			const uniqueFormattedDates = filteredEvents.reduce((dates, event) => {
				const formattedDate = moment(event?.startTime).format("DD.MM");
				const existingDate = dates.find((date) => date?.date === formattedDate);

				if (!existingDate) {
					dates.push({
						date: formattedDate,
						status: event?.status,
					});
				}

				return dates;
			}, []);

			const firstDate = uniqueFormattedDates[0]?.date;
			const firstEventDate = moment(filteredEventData[0]?.startTime).format(
				"DD.MM"
			);

			if (firstDate === firstEventDate) {
				setViewingFirstEvent(true);
			}

			setViewingLastEvent(false);
			setDates(uniqueFormattedDates);
		}

		setViewSkipButton(true);
	}, [currentMode, filteredEventData, dateRangeStart]);

	// View Events Forwards:
	const viewForwardEvents = useCallback(() => {
		if (currentMode === "year") {
			const newDateRangeStart = dateRangeEnd
				.clone()
				.add(1, "day")
				.startOf("day");
			const newDateRangeEnd = newDateRangeStart
				.clone()
				.add(6, "days")
				.endOf("day");

			setDateRangeStart(newDateRangeStart);
			setDateRangeEnd(newDateRangeEnd);

			const formattedDateRange = `${newDateRangeStart.format(
				"D MMM"
			)} - ${newDateRangeEnd.format("D MMM YYYY")}`;

			setViewedCalendarYear(newDateRangeStart.format("YYYY"));
			setHeaderDateRange(formattedDateRange);

			const filteredEvents = filteredEventData?.filter((event) => {
				const eventDate = moment.utc(event?.startTime);
				return eventDate.isBetween(
					newDateRangeStart,
					newDateRangeEnd,
					"day",
					"[]"
				);
			});

			const uniqueFormattedDates = filteredEvents.reduce((dates, event) => {
				const formattedDate = moment(event?.startTime).format("DD.MM");
				const existingDate = dates.find((date) => date?.date === formattedDate);

				if (!existingDate) {
					dates.push({
						date: formattedDate,
						status: event?.status,
					});
				}

				return dates;
			}, []);

			const lastDate =
				uniqueFormattedDates[uniqueFormattedDates.length - 1]?.date;
			const lastEventDate = moment(
				filteredEventData[filteredEventData?.length - 1]?.startTime
			).format("DD.MM");

			if (lastDate === lastEventDate) {
				setViewingLastEvent(true);
			}

			setViewingFirstEvent(false);
			setDates(uniqueFormattedDates);
		}
	}, [currentMode, filteredEventData, dateRangeEnd]);

	// Handle View Event:
	const handleViewEvent = (event) => {
		setShowModal(true);
		setModalContent(
			<ViewEvent
				event={event}
				title="View Event"
				onClose={() => setShowModal(false)}
				handleEditEvent={handleEditEvent}
			/>
		);
	};

	// Handle Edit Event:
	const handleEditEvent = (event) => {
		setShowModal(true);
		setModalContent(
			<EditEvent
				event={event}
				title="Edit Event"
				onClose={() => setShowModal(false)}
				handleShowSuccessModal={handleShowSuccessModal}
			/>
		);
	};

	// Handle Show Success Modal:
	const handleShowSuccessModal = () => {
		setShowModal(true);
		setModalContent(
			<SuccessModal
				mode="fromEdit"
				title="Event Saved"
				message="Event has been saved successfully"
				onClose={() => setShowModal(false)}
			/>
		);
	};

	// Side Effect | Event List:
	useEffect(() => {
		if (location?.pathname === "/dashboard/event-list") {
			setView("Event List");
		}

		document.title = "ED | Event List";
	}, [location.pathname]);

	return (
		<>
			<section className="event_list">
				<div className="event_list_container">
					{/* Header */}
					<DateToggleControl
						view={view}
						data={filteredEventData}
						currentMode={currentMode}
						statusFilter={statusFilter}
						headerDateRange={headerDateRange}
						viewBackwardsEvents={viewBackwardsEvents}
						viewForwardEvents={viewForwardEvents}
						viewingFirstEvent={viewingFirstEvent}
						viewingLastEvent={viewingLastEvent}
					/>

					{/* Content */}
					<div className="event_list_content">
						<div className="event_list_content_headers">
							<div className="event_list_content_headers_view_toggler">
								<button
									className={
										!filteredEventData.length
											? "mode_view_toggler_disabled"
											: ""
									}
									onClick={toggleModes}
								>
									{loadingAllEvents ? <DarkLoader /> : `${nextMode} View`}
								</button>
							</div>
							<div className="event_list_content_headers_event_indicator">
								<div>
									<span
										className={`event_list_pending_indicator ${
											statusFilter === "pending" || statusFilter === "all"
												? "active_status"
												: ""
										}`}
										onClick={() => handleViewByStatus("pending")}
									>
										Pending
									</span>
									<span
										className={`event_list_happening_indicator ${
											statusFilter === "happening" || statusFilter === "all"
												? "active_status"
												: ""
										}`}
										onClick={() => handleViewByStatus("happening")}
									>
										Happening
									</span>
									<span
										className={`event_list_past_indicator ${
											statusFilter === "past" || statusFilter === "all"
												? "active_status"
												: ""
										}`}
										onClick={() => handleViewByStatus("past")}
									>
										Past Events
									</span>
								</div>
							</div>
						</div>

						{/* Content Body */}
						{loadingAllEvents ? (
							<div className="event_list_content_info">
								<div className="event_list_content_info_events">
									<div className="event_list_content_info_date">
										{Array.from({ length: 5 }).map((_, index) => (
											<Skeleton
												key={index}
												animation="wave"
												variant="rectangular"
												className="event_list_content_info_date_skeleton"
											/>
										))}
									</div>

									<div className="event_list_content_info_body">
										{Array.from({ length: 5 }).map((_, index) => (
											<div className="event_list_card_container" key={index}>
												<div className="event_card_skeleton">
													<div className="event_card_header">
														<div className="event_card_header_for_event_and_time_skeleton">
															<Skeleton
																variant="text"
																animation="wave"
																className="event_list_content_info_date_skeleton_h3"
															/>
															<Skeleton
																variant="text"
																animation="wave"
																className="event_list_content_info_date_skeleton_p"
															/>
														</div>
														<div className="event_card_header_for_icon_and_participants_skeleton">
															<Skeleton
																variant="text"
																animation="wave"
																className="event_mode_pill_skeleton"
															/>
															<Skeleton
																variant="circular"
																animation="wave"
																className="event_card_participate_icon_skeleton"
															/>
															<Skeleton
																variant="text"
																animation="wave"
																className="event_card_header_for_icon_and_participants_skeleton_p"
															/>
														</div>
													</div>
													<div className="event_card_body_skeleton">
														<Skeleton
															variant="text"
															animation="wave"
															className="event_card_body_skeleton_p"
														/>
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						) : (
							<div className="event_list_content_info">
								{filteredEventData?.length ? (
									<div className="event_list_content_info_events">
										<div className="event_list_content_info_date">
											{dates?.map((data, index) => {
												const isToday = data?.date === moment().format("DD.MM");

												return (
													<span
														ref={isToday ? dateRef : null}
														key={index}
														className={getClassForDate(data)}
													>
														{currentMode === "week"
															? `Week ${data?.date - 1}`
															: currentMode === "quarter"
																? `${getOrdinal(data?.date)} Quarter`
																: data?.date}

														{getClassForDate(data) ===
															"event_date_indicator_happening" && (
															<span className="current_date_indicator">
																<span className="current_date_indicator_head"></span>
															</span>
														)}
													</span>
												);
											})}
										</div>

										<div className="event_list_content_info_body">
											{dates.map((data, index) => (
												<div className="event_list_card_container" key={index}>
													{filteredEvents.map((event) => {
														let isEventMatching = false;

														switch (currentMode) {
															case "year":
																isEventMatching =
																	moment
																		.utc(event?.startTime)
																		.format("DD.MM") === data?.date;
																break;
															case "week":
																isEventMatching =
																	moment.utc(event?.startTime).week() ===
																	data?.date;
																break;
															case "month":
																isEventMatching =
																	moment.utc(event?.startTime).format("MMM") ===
																	data?.date;
																break;
															case "quarter":
																isEventMatching =
																	moment.utc(event?.startTime).quarter() ===
																	data?.date;
																break;
															default:
																break;
														}

														if (isEventMatching) {
															return (
																<EventCard
																	event={event}
																	key={event?.id}
																	currentMode={currentMode}
																	handleViewEvent={handleViewEvent}
																/>
															);
														}

														return null;
													})}
												</div>
											))}
										</div>
									</div>
								) : (
									<div className="event_list_content_info_events">
										<div className="event_list_content_info_date">
											<span className="no_event">
												{moment(currentDate).format("DD.MM")}
											</span>
										</div>

										<div className="event_list_content_info_body">
											<div className="event_list_card_container">
												<div className="no_event_event_card">
													No event available
												</div>
											</div>
										</div>
									</div>
								)}
							</div>
						)}

						{viewSkipButton && currentMode === "year" ? (
							<button
								className={`event_jump_button ${
									viewingLastEvent ? "disable" : ""
								}`}
								onClick={viewForwardEvents}
							>
								Next Week <FaAnglesDown className="event_jump_button_icon" />
							</button>
						) : null}
					</div>
				</div>
			</section>

			{/* Modal */}
			{showModal && <Modal modalContent={modalContent} />}

			<ToastContainer />
		</>
	);
}

export default EventList;
